// GuidedChooser — a small modal shown by an entity's "New …/Add …" button,
// offering Manual (the existing create form) or Guided (launch the entity's
// GuidedFlow walkthrough). Mirrors the New-Project chooser so every create entry
// point gets a consistent Manual | Guided choice. Existing manual forms are
// untouched — the chooser just gates which one opens.
import React, { useState } from "react";
import { FilePlus2, Sparkles, X, ChevronLeft } from "lucide-react";

// Launch a per-entity guide (mounted in Layout via the cortex:start-guided event).
// `kind` optionally tailors the guide to a specific source type (file, postgres,
// mysql, snowflake, …) so the assistant is prompted for that type's fields.
export function startGuided(guideKey, kind) {
  window.dispatchEvent(new CustomEvent("cortex:start-guided", { detail: { guide: guideKey, kind: kind || null } }));
}

export default function GuidedChooser({
  title,
  manualLabel = "Manual",
  manualHint,
  guidedHint,
  onManual,
  onGuided,        // () => void  OR  (kind) => void when guidedTypes is given
  onClose,
  guidedTypes,     // optional [{ value, label }] — when set, Guided reveals a type picker
  // Optional third route, for entities where the thing you want to create may
  // not exist as a TYPE yet (data sources: the system isn't in the catalog).
  // { label, hint, icon, onSelect, pickerLabel } — `pickerLabel` also puts it
  // under the type grid, which is where the user actually discovers the gap.
  extraOption,
  // Open straight on the type grid. Used when returning here from a nested
  // modal that was launched FROM the grid — landing on the first screen would
  // silently undo the step the user had already taken.
  initialPickingType = false,
}) {
  const ExtraIcon = extraOption?.icon || Sparkles;
  const [pickingType, setPickingType] = useState(initialPickingType);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="w-[440px] max-w-[calc(100vw-2rem)] rounded-[10px] bg-card-bg border border-page-divider shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-page-divider">
          <div className="flex items-center gap-2 text-sm font-semibold">
            {pickingType && <button onClick={() => setPickingType(false)} className="p-0.5 rounded hover:bg-page-bg" title="Back"><ChevronLeft size={16} /></button>}
            {pickingType ? "Choose a source type" : title}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-page-bg" title="Cancel"><X size={16} /></button>
        </div>
        {pickingType ? (
          <div className="max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-2 p-4">
              {(guidedTypes || []).map((t) => (
                <button key={t.value} onClick={() => onGuided(t.value)}
                  className="flex items-center gap-2 text-left p-3 rounded-[8px] border border-page-divider hover:border-nav-active hover:bg-page-bg/50 transition-colors">
                  <Sparkles size={16} className="shrink-0 text-nav-active" />
                  <span className="text-sm font-medium">{t.label}</span>
                </button>
              ))}
            </div>
            {/* Scanning this grid and not finding your system IS the moment the
                build route is wanted — so it lives here, not only one screen up. */}
            {extraOption?.pickerLabel && (
              <div className="px-4 pb-4 -mt-1">
                <button onClick={() => extraOption.onSelect(true)}
                  className="w-full flex items-center justify-center gap-2 p-2.5 rounded-[8px] border border-dashed border-page-divider text-page-muted hover:border-nav-active hover:text-page-text hover:bg-page-bg/50 transition-colors">
                  <ExtraIcon size={15} className="shrink-0" />
                  <span className="text-xs font-medium">{extraOption.pickerLabel}</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3 p-4">
            <button onClick={onManual}
              className="flex items-start gap-3 text-left p-3 rounded-[8px] border border-page-divider hover:border-nav-active hover:bg-page-bg/50 transition-colors">
              <FilePlus2 size={22} className="shrink-0 mt-0.5 text-page-muted" />
              <div>
                <div className="font-medium text-sm">{manualLabel}</div>
                <div className="text-xs text-page-muted">{manualHint || "Fill in the form yourself."}</div>
              </div>
            </button>
            <button onClick={() => { if (guidedTypes && guidedTypes.length) setPickingType(true); else onGuided(); }}
              className="flex items-start gap-3 text-left p-3 rounded-[8px] border border-page-divider hover:border-nav-active hover:bg-page-bg/50 transition-colors">
              <Sparkles size={22} className="shrink-0 mt-0.5 text-nav-active" />
              <div>
                <div className="font-medium text-sm">Guided</div>
                <div className="text-xs text-page-muted">{guidedHint || "Let the assistant walk you through it, step by step, in a slide panel — drive it with Next or auto-play."}</div>
              </div>
            </button>
            {extraOption && (
              <button onClick={() => extraOption.onSelect(false)} data-testid="chooser-extra"
                className="flex items-start gap-3 text-left p-3 rounded-[8px] border border-page-divider hover:border-nav-active hover:bg-page-bg/50 transition-colors">
                <ExtraIcon size={22} className="shrink-0 mt-0.5 text-nav-active" />
                <div>
                  <div className="font-medium text-sm">{extraOption.label}</div>
                  <div className="text-xs text-page-muted">{extraOption.hint}</div>
                </div>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
